import React, { useState, useEffect } from "react";
import { UserGrossPayInput, CompanyGrossPayView, UserGrossPayView, AmountCalculationType } from "../../../../models/payroll";
import YearMonthPicker from "../../../../components/yearMonthPicker";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from "../../../../components/ui/Dialog";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import Button from "../../../../components/ui/Button";

interface UserGrossPayDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: UserGrossPayInput) => void;
  companyGrossPays: CompanyGrossPayView[];
  editGrossPay: UserGrossPayView | null;
  existingGrossPays: UserGrossPayView[];
  errorMessage: string | null;
  defaultYear?: number;
  defaultMonth?: number;
}

const UserGrossPayDialog: React.FC<UserGrossPayDialogProps> = ({ open, onClose, onSave, companyGrossPays, editGrossPay, existingGrossPays, errorMessage, defaultYear, defaultMonth }) => {
  const { t } = useTranslation('common');
  const [remarks, setRemarks] = useState<string>("");
  const [amount, setAmount] = useState<number>(0);
  const [companyGrossPayId, setCompanyGrossPayId] = useState<number>(0);
  const [companyGrossPay, setCompanyGrossPay] = useState<CompanyGrossPayView | undefined>(companyGrossPays[0]);
  const [year, setYear] = useState<number>(defaultYear || new Date().getFullYear());
  const [month, setMonth] = useState<number>(defaultMonth || new Date().getMonth());

  const isEditing = !!editGrossPay?.id;
  const isDuplicate = !isEditing && companyGrossPayId > 0 &&
    existingGrossPays.some(g =>
      g.companyGrossPayId === companyGrossPayId &&
      new Date(g.effectiveStartDate).getMonth() === month &&
      new Date(g.effectiveStartDate).getFullYear() === year
    );

  useEffect(() => {
    if (editGrossPay?.id) {
      setRemarks(editGrossPay.remarks || "");
      setAmount(editGrossPay.amount);
      setCompanyGrossPayId(editGrossPay.companyGrossPayId ?? 0);
      setCompanyGrossPay(companyGrossPays.find((x) => x.id === editGrossPay.companyGrossPayId));
    } else {
      setRemarks("");
      setAmount(0);
      setCompanyGrossPayId(0);
    }
    setYear(defaultYear || new Date().getFullYear());
    setMonth(defaultMonth || new Date().getMonth());
  }, [editGrossPay, defaultYear, defaultMonth]);

  const handleYearMonthChange = (date: Date) => {
    setYear(date.getFullYear());
    setMonth(date.getMonth());
  };

  const handleSave = () => {
    const input: UserGrossPayInput = {
      id: editGrossPay?.id || 0,
      userId: editGrossPay?.userId || "",
      companyGrossPayId: companyGrossPayId,
      remarks,
      amount,
      effectiveStartDate: year + "-" + (month + 1) + "-01",
      effectiveStopDate: undefined
    };
    onSave(input);
  };

  const grossPayOptions = companyGrossPays.map((x) => ({ label: x.name, value: x.id }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{editGrossPay?.id ? t("Edit Gross Pay") : t("Add Gross Pay")}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isDuplicate && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-700 dark:text-amber-400 mb-4">
              {t("This gross pay already exists for the selected period. Please update the existing entry instead of adding a new one.")}
            </div>
          )}
          {errorMessage && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive mb-4">
              {errorMessage}
            </div>
          )}
          <div className="flex flex-col gap-4">
            <Select
              label={t("Company Gross Pay")}
              options={grossPayOptions}
              value={companyGrossPayId || undefined}
              onChange={(value: any) => {
                setCompanyGrossPayId(value ?? 0);
                setCompanyGrossPay(companyGrossPays.find((x) => x.id === value));
              }}
              searchable
            />
            {companyGrossPay?.amountCalculationType === AmountCalculationType.EmployeeSpecificAmount && (
              <Input
                label={t("Amount")}
                type="number"
                value={amount}
                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setAmount(parseFloat(ev.target.value))}
              />
            )}
            <Input
              label={t("Remarks")}
              value={remarks}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setRemarks(ev.target.value)}
            />
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">
                {t("Takes effect from date")}
              </p>
              <YearMonthPicker
                date={new Date(year, month)}
                onChange={handleYearMonthChange}
                fromMonth={new Date(new Date().getFullYear() - 8, 0, 1)}
                toMonth={new Date(new Date().getFullYear() + 1, 11, 31)}
                compact
                isRtl={false}
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <div className="flex justify-center gap-3 w-full">
            <Button variant="default" onClick={handleSave} disabled={isDuplicate}>
              {t("Submit")}
            </Button>
            <Button variant="outline" onClick={onClose}>
              {t("Cancel")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserGrossPayDialog;
